import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import DynamicFieldInput from '../components/DynamicFieldInput';
import AssigneePicker from '../components/AssigneePicker';
import SimilarTickets from '../components/SimilarTickets';
import {
  getTickets,
  getTicketTags,
  getTicketViews,
  createTicketView,
  deleteTicketView,
  getCustomers,
  getTicketProjectFields,
  getTicketProjects,
  createTicket,
  updateTicket,
  updateTicketStatus,
  getUsers,
  errorMessage,
} from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { fmtDateDMY, fmtRelativeDate, fmtDateTime } from '../utils/format';
import { slaText, SLA_CHIP_CLASS } from '../utils/ticketSla';
import { dueDateStatus } from '../utils/ticketDue';
import { canAction } from '../menu';
import { NtIcon, Pill, PageHeading, Th, STATUS_COLORS, PRIORITY_COLORS } from '../components/NotionUI';
import { TagChips } from '../components/TicketTags';
import Select from '../components/Select';
import SearchBox from '../components/KernelSearchBox';
import Highlight from '../components/KernelHighlight';
import { searchTerms, matchesAllTerms } from '../utils/kernelSearch';
import { userColor, customerColor, customerInitials } from '../utils/userColor';

const STATUSES = ['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

const emptyForm = { project_id: '', subject: '', description: '', priority: 'medium', assignee_id: '', due_at: '' };

const DEFAULT_VIEW_KEY = 'tickets_default_view';

function getDefaultView() {
  const saved = localStorage.getItem(DEFAULT_VIEW_KEY);
  return saved === 'board' ? 'board' : 'list';
}

function Tickets({ user, onLogout }) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [tickets, setTickets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [agents, setAgents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tagList, setTagList] = useState([]);
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState(getDefaultView);
  const [defaultView, setDefaultView] = useState(getDefaultView);
  const [filters, setFilters] = useState({ project_id: searchParams.get('project') || '', status: '', priority: '', assignee_id: '', group_id: searchParams.get('group') || '', tag_id: '', sla: searchParams.get('sla') || '' });
  const [viewTouched, setViewTouched] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(null);
  const [search, setSearch] = useState('');
  const [dynamicFields, setDynamicFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v && k !== 'status') params[k] = v;
      });
      const data = await getTickets(params);
      setTickets(data.tickets || []);
    } catch (err) {
      setError(errorMessage(err, t('tickets.loadError')));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.project_id, filters.priority, filters.assignee_id, filters.group_id, filters.tag_id, filters.sla]);

  useEffect(() => {
    getTicketProjects()
      .then((d) => setProjects(d.projects || []))
      .catch(() => {});
    getUsers()
      .then((d) => setAgents(d.users || []))
      .catch(() => {});
    getTicketTags().then((d) => setTagList(d.tags || [])).catch(() => {});
    getTicketViews().then((d) => setViews(d.views || [])).catch(() => {});
    // I gruppi di clienti servono solo al filtro: chi non può vedere i clienti non lo ha.
    if (canAction(user, 'customers.view')) {
      getCustomers()
        .then((d) => setGroups(d.groups || []))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Filtrando per un progetto si apre la sua vista predefinita (impostata alla
  // creazione da modello o dalla modifica del progetto), a meno che l'utente
  // non abbia già scelto lui una vista in questa pagina.
  useEffect(() => {
    if (viewTouched || !filters.project_id) return;
    const project = projects.find((p) => String(p.id) === String(filters.project_id));
    if (project?.default_view) setView(project.default_view);
  }, [filters.project_id, projects, viewTouched]);

  const handleProjectChange = (projectId) => {
    setForm((f) => ({ ...f, project_id: projectId }));
    setFieldValues({});
    if (!projectId) {
      setDynamicFields([]);
      return;
    }
    getTicketProjectFields(projectId)
      .then((d) => setDynamicFields(d.fields || []))
      .catch(() => setDynamicFields([]));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, field_values: fieldValues };
      if (!payload.assignee_id) delete payload.assignee_id;
      if (!payload.due_at) delete payload.due_at;
      const res = await createTicket(payload);
      setShowForm(false);
      setForm(emptyForm);
      setDynamicFields([]);
      setFieldValues({});
      fetchTickets();
      if (res.ticket) navigate(`/tickets/${res.ticket.id}`);
    } catch (err) {
      setError(errorMessage(err, t('tickets.createError')));
    } finally {
      setSaving(false);
    }
  };

  const saveAsDefaultView = () => {
    localStorage.setItem(DEFAULT_VIEW_KEY, view);
    setDefaultView(view);
  };

  const moveStatus = async (id, status) => {
    setTickets((prev) => prev.map((tk) => (tk.id === id ? { ...tk, status } : tk)));
    try {
      await updateTicketStatus(id, status);
    } catch (err) {
      setError(errorMessage(err));
      fetchTickets();
    }
  };

  const changeAssignee = async (id, assigneeId) => {
    const agent = agents.find((a) => a.id === assigneeId) || null;
    setTickets((prev) =>
      prev.map((tk) =>
        tk.id === id
          ? {
            ...tk,
            assignee_id: assigneeId,
            assignee_name: agent?.name || null,
            assignee_surname: agent?.surname || null,
            // chi diventa responsabile esce dai collaboratori
            collaborators: (tk.collaborators || []).filter((c) => c.id !== assigneeId),
          }
          : tk
      )
    );
    try {
      await updateTicket(id, { assignee_id: assigneeId });
    } catch (err) {
      setError(errorMessage(err));
      fetchTickets();
    }
  };

  // Ricerca e stato si applicano qui, sull'elenco già caricato.
  // Più parole separate da spazio: devono comparire tutte (AND), anche in colonne diverse. Si cerca solo nel
  // testo che si vede nella vista corrente (elenco o board), non nelle date; un numero (anche "#12") corrisponde all'id.
  const terms = searchTerms(search);
  const idMatches = (tk) => terms.some((term) => String(tk.id) === term.replace(/^#/, ''));
  const visibleText = (tk) => [
    tk.subject,
    tk.customer_email || t('tickets.requesterStaff'),
    tk.group_name || '',
    tk.project_name,
    t(`tickets.priority_${tk.priority}`),
    view === 'list' ? t(`tickets.status_${tk.status}`) : '',
    view === 'list' ? (tk.assignee_id ? [tk.assignee_name, tk.assignee_surname].filter(Boolean).join(' ') : t('tickets.unassigned')) : '',
  ].join(' ');
  const searched = tickets.filter((tk) => {
    if (terms.length === 0) return true;
    const text = visibleText(tk);
    return terms.every((term) => String(tk.id) === term.replace(/^#/, '') || matchesAllTerms(text, [term]));
  });
  const statusCounts = Object.fromEntries(STATUSES.map((st) => [st, searched.filter((tk) => tk.status === st).length]));
  const visible = filters.status ? searched.filter((tk) => tk.status === filters.status) : searched;

  const startNewTicket = () => {
    setForm(emptyForm);
    setShowForm(true);
    handleProjectChange(projects[0]?.id || '');
  };


  // Collaboratori del ticket: dopo un "+", avatar piccoli a bordo tratteggiato, uno per persona e con il suo colore.
  const collabStack = (tk) => {
    const list = tk.collaborators || [];
    if (list.length === 0) return null;
    const names = list.map((c) => [c.name, c.surname].filter(Boolean).join(' ')).join(', ');
    return (
      <span className="collab-stack" title={`${t('tickets.collaborators')}: ${names}`}>
        <span className="collab-stack-plus" aria-hidden="true">+</span>
        {list.slice(0, 3).map((c) => (
          <span key={c.id} className="collab-stack-av" style={{ background: userColor(c) }}>
            {`${(c.name || '')[0] || ''}${(c.surname || '')[0] || ''}`.toUpperCase()}
          </span>
        ))}
        {list.length > 3 && <span className="collab-stack-av collab-stack-more">+{list.length - 3}</span>}
      </span>
    );
  };

  const applyView = (id) => {
    const view = views.find((v) => String(v.id) === String(id));
    if (!view) return;
    const f = view.filters || {};
    setFilters({ project_id: f.project_id || '', status: f.status || '', priority: f.priority || '', assignee_id: f.assignee_id || '', group_id: f.group_id || '', tag_id: f.tag_id || '', sla: f.sla || '' });
    setSearch(f.search || '');
  };

  const saveView = async () => {
    const name = window.prompt(t('tickets.viewNamePrompt'));
    if (!name || !name.trim()) return;
    const shared = canAction(user, 'ticket_projects.manage') && window.confirm(t('tickets.viewSharePrompt'));
    try {
      const res = await createTicketView({ name: name.trim(), filters: { ...filters, search }, is_shared: shared });
      setViews(res.views || []);
    } catch (err) {
      setError(errorMessage(err, t('tickets.viewSaveError')));
    }
  };

  const removeView = async (id) => {
    try {
      await deleteTicketView(id);
      setViews(views.filter((v) => v.id !== id));
    } catch (err) {
      setError(errorMessage(err, t('tickets.viewSaveError')));
    }
  };

  const initialsOf = (tk) => `${(tk.assignee_name || '')[0] || ''}${(tk.assignee_surname || '')[0] || ''}`.toUpperCase();

  const dueChip = (tk, dueStatus) => (
    <span
      className={`tk-due tk-due-${dueStatus}`}
      title={dueStatus === 'overdue' ? t('tickets.overdue') : dueStatus === 'warning' ? t('tickets.dueSoon') : undefined}
    >
      <NtIcon name="clock" size={12} /> {fmtRelativeDate(tk.due_at)}
    </span>
  );

  // Stato SLA (prima risposta o risoluzione, la più urgente): quanto manca o di quanto è in ritardo.
  const slaChip = (tk) => (
    <span
      className={`tk-due tk-due-${SLA_CHIP_CLASS[tk.sla.state]}`}
      title={`${t(tk.sla.kind === 'first' ? 'tickets.slaFirst' : 'tickets.slaResolution')}: ${fmtDateTime(tk.sla.at)}`}
    >
      <NtIcon name="clock" size={12} /> {slaText(tk.sla, t)}
    </span>
  );

  const priorityTag = (priority) => (
    <span className="tk-prio" style={{ '--c': PRIORITY_COLORS[priority] }}>
      <i aria-hidden="true" /> <Highlight text={t(`tickets.priority_${priority}`)} terms={terms} />
    </span>
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page">
      <PageHeading icon="ticket" title={t('tickets.title')} subtitle={t('tickets.subtitle')} />

      {!showForm && (
      <div className="tk-bar">
        <div className="pj-view-toggle" role="group" aria-label={t('tickets.viewMode')}>
          {[['list', 'table', t('tickets.viewList')], ['board', 'board', t('tickets.viewBoard')]].map(([key, icon, label]) => (
            <button
              key={key}
              type="button"
              className={'pj-view-btn tk-view-btn' + (view === key ? ' pj-view-active' : '')}
              aria-pressed={view === key}
              onClick={() => {
                setViewTouched(true);
                setView(key);
              }}
            >
              <NtIcon name={icon} /> {label}
            </button>
          ))}
        </div>
        <span className="tk-bar-spacer" />
        {view !== defaultView && (
          <button type="button" className="nt-ghost-btn" onClick={saveAsDefaultView} title={t('tickets.setDefaultViewHint')}>
            {t('tickets.setDefaultView')}
          </button>
        )}
        <button className="btn" type="button" onClick={startNewTicket} disabled={projects.length === 0}>
          + {t('tickets.newTicket')}
        </button>
      </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <form onSubmit={handleCreate} className="tp-form tk-form">
          <h2 className="tp-form-title">{t('tickets.newTicket')}</h2>
          <section className="tp-section">
            <div className="tp-fields">
              <div className="form-group tp-full">
                <label>{t('tickets.formProject')}</label>
                <Select
                  value={form.project_id}
                  onChange={(e) => handleProjectChange(Number(e.target.value))}
                  required
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.code ? `${p.name} (${p.code})` : p.name}</option>
                  ))}
                </Select>
              </div>
              {dynamicFields
                .filter((field) => field.core_key !== 'name' && field.core_key !== 'email')
                .map((field) =>
                  field.core_key ? (
                    <DynamicFieldInput
                      key={field.id}
                      field={field}
                      value={form[field.core_key]}
                      onChange={(value) => setForm({ ...form, [field.core_key]: value })}
                      locale={lang}
                    />
                  ) : (
                    <DynamicFieldInput
                      key={field.id}
                      field={field}
                      value={fieldValues[field.id]}
                      onChange={(value) => setFieldValues({ ...fieldValues, [field.id]: value })}
                      locale={lang}
                    />
                  )
                )}
            </div>
          </section>
          <SimilarTickets text={`${form.subject || ''} ${form.description || ''}`} from="new" title={t('tickets.similarTitleNew')} />
          <section className="tp-section">
            <div className="tp-fields">
              <div className="form-group">
                <label>{t('tickets.formPriority')}</label>
                <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>{t(`tickets.priority_${p}`)}</option>
                  ))}
                </Select>
              </div>
              <div className="form-group">
                <label>{t('tickets.formAssignee')}</label>
                <Select value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}>
                  <option value="">{t('tickets.unassigned')}</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>{[a.name, a.surname].filter(Boolean).join(' ')}{a.id === user.id ? ` (${t('tickets.you')})` : ''}</option>
                  ))}
                </Select>
              </div>
              <div className="form-group">
                <label>{t('tickets.formDueAt')}</label>
                <input type="date" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} />
              </div>
            </div>
          </section>
          <div className="tp-savebar">
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn" disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      )}

      {!showForm && (<>
      <div className="tk-filters">
        <SearchBox className="tk-search" value={search} onChange={setSearch} placeholder={t('tickets.searchPlaceholder')} title={t('tickets.searchHint')} />
        <label className="nt-filter">
          <NtIcon name="tag" />
          <span>{t('tickets.filterProject')}</span>
          <Select value={filters.project_id} onChange={(e) => setFilters({ ...filters, project_id: e.target.value })}>
            <option value="">{t('tickets.anyValue')}</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.code ? `${p.name} (${p.code})` : p.name}</option>
            ))}
          </Select>
        </label>
        <label className="nt-filter">
          <NtIcon name="priority" />
          <span>{t('tickets.filterPriority')}</span>
          <Select value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })}>
            <option value="">{t('tickets.anyValue')}</option>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>{t(`tickets.priority_${p}`)}</option>
            ))}
          </Select>
        </label>
        <label className="nt-filter">
          <NtIcon name="clock" />
          <span>{t('tickets.filterSla')}</span>
          <Select value={filters.sla} onChange={(e) => setFilters({ ...filters, sla: e.target.value })}>
            <option value="">{t('tickets.anyValue')}</option>
            <option value="late">{t('tickets.slaFilterLate')}</option>
            <option value="soon">{t('tickets.slaFilterSoon')}</option>
          </Select>
        </label>
        {(groups.length > 0 || filters.group_id) && (
          <label className="nt-filter">
            <NtIcon name="users" />
            <span>{t('tickets.filterGroup')}</span>
            <Select searchable value={filters.group_id} onChange={(e) => setFilters({ ...filters, group_id: e.target.value })}>
              <option value="">{t('tickets.anyValue')}</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </Select>
          </label>
        )}
        {(tagList.length > 0 || filters.tag_id) && (
          <label className="nt-filter">
            <NtIcon name="tag" />
            <span>{t('tickets.filterTag')}</span>
            <Select value={filters.tag_id} onChange={(e) => setFilters({ ...filters, tag_id: e.target.value })}>
              <option value="">{t('tickets.anyValue')}</option>
              {tagList.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </Select>
          </label>
        )}
        <label className="nt-filter">
          <input
            type="checkbox"
            checked={filters.assignee_id === user.id}
            onChange={(e) => setFilters({ ...filters, assignee_id: e.target.checked ? user.id : '' })}
          />
          {t('tickets.filterMine')}
        </label>
      </div>

      <div className="tk-views">
        <span className="nt-muted">{t('tickets.views')}</span>
        {views.map((v) => (
          <span key={v.id} className="tk-view-chip">
            <button type="button" onClick={() => applyView(v.id)} title={v.is_shared ? t('tickets.viewShared', { owner: v.owner }) : undefined}>{v.is_shared ? '👥 ' : ''}{v.name}</button>
            {(v.is_mine || canAction(user, 'ticket_projects.manage')) && <button type="button" className="tk-view-x" onClick={() => removeView(v.id)} aria-label={t('common.delete')}>×</button>}
          </span>
        ))}
        <button type="button" className="tk-view-add" onClick={saveView}><span aria-hidden="true">+</span> {t('tickets.saveView')}</button>
      </div>

      <div className="tpl-cats tk-status-tabs" role="group" aria-label={t('tickets.filterStatus')}>
        <button type="button" className={'tpl-cat' + (!filters.status ? ' tpl-cat-active' : '')} onClick={() => setFilters({ ...filters, status: '' })}>
          {t('tickets.anyValue')} <span className="tpl-cat-count">{searched.length}</span>
        </button>
        {STATUSES.map((s) => (
          <button key={s} type="button" className={'tpl-cat' + (filters.status === s ? ' tpl-cat-active' : '')} onClick={() => setFilters({ ...filters, status: filters.status === s ? '' : s })}>
            <span className="tk-dot" style={{ background: STATUS_COLORS[s] }} aria-hidden="true" /> {t(`tickets.status_${s}`)} <span className="tpl-cat-count">{statusCounts[s]}</span>
          </button>
        ))}
        {!loading && <span className="nt-muted tk-total">{t('tickets.countLabel', { count: visible.length })}</span>}
      </div>

      {loading ? (
        <div className="loading">{t('common.loading')}</div>
      ) : view === 'list' ? (
        <div className="cu-card tk-card">
          {visible.length === 0 ? (
            <p className="nt-empty">{t('tickets.noResults')}</p>
          ) : (
            <div className="nt-table-wrap">
              <table className="nt-table cu-table tk-table">
                <thead>
                  <tr>
                    <Th icon="hash" />
                    <Th icon="text">{t('tickets.subject')}</Th>
                    <Th icon="user">{t('tickets.requester')}</Th>
                    <Th icon="tag">{t('tickets.projectColumn')}</Th>
                    <Th icon="priority">{t('tickets.priority')}</Th>
                    <Th icon="status">{t('tickets.status')}</Th>
                    <Th icon="users">{t('tickets.assignee')}</Th>
                    <Th icon="clock">{t('tickets.dueAt')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((tk) => {
                    const dueStatus = dueDateStatus(tk);
                    return (
                      <tr key={tk.id} className="nt-clickable" style={{ '--q': tk.project_color || 'transparent' }} onClick={() => navigate(`/tickets/${tk.id}`)}>
                        <td className="nt-cell-id tk-id" style={{ boxShadow: `inset 4px 0 0 ${tk.project_color || 'transparent'}` }}>{idMatches(tk) ? <mark className="khl">{tk.id}</mark> : tk.id}</td>
                        <td>
                          <div className="tk-subject"><Highlight text={tk.subject} terms={terms} /> <TagChips tags={tk.tags} onPick={(tag) => setFilters({ ...filters, tag_id: String(tag.id) })} /></div>
                          <div className="tk-requester">{fmtDateDMY(tk.created_at)}</div>
                        </td>
                        <td>
                          {tk.customer_email ? (
                            <div className="tk-req-row">
                              <span className="avatar avatar-sm" style={{ background: customerColor({ email: tk.customer_email, name: tk.customer_name }) }} aria-hidden="true">
                                {customerInitials({ email: tk.customer_email, name: tk.customer_name })}
                              </span>
                              <div className="tk-req-cell">
                                <span className="tk-req-email"><Highlight text={tk.customer_email} terms={terms} /></span>
                                {tk.group_name && (
                                  <span className="cg-tag cg-tag-sm" style={{ '--pill': tk.group_color }} title={t('tickets.customerGroup')}>
                                    <i className="cg-dot" aria-hidden="true" /><Highlight text={tk.group_name} terms={terms} />
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="nt-muted"><Highlight text={t('tickets.requesterStaff')} terms={terms} /></span>
                          )}
                        </td>
                        <td>
                          <Pill color={tk.project_color}><Highlight text={tk.project_name} terms={terms} /></Pill>
                        </td>
                        <td>{priorityTag(tk.priority)}</td>
                        <td>
                          <Pill dot color={STATUS_COLORS[tk.status]}><Highlight text={t(`tickets.status_${tk.status}`)} terms={terms} /></Pill>
                        </td>
                        <td>
                          <div className="tk-assignees">
                            <AssigneePicker
                              value={tk.assignee_id}
                              agents={agents}
                              me={user.id}
                              terms={terms}
                              onChange={(assigneeId) => changeAssignee(tk.id, assigneeId)}
                            />
                            {collabStack(tk)}
                          </div>
                        </td>
                        <td>{tk.sla ? slaChip(tk) : tk.due_at ? dueChip(tk, dueStatus) : <span className="nt-muted">—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <button type="button" className="nt-add-row tk-add" onClick={startNewTicket} disabled={projects.length === 0}>
            <span aria-hidden="true">+</span> {t('tickets.newTicket')}
          </button>
        </div>
      ) : (
        <div className="kanban-board tk-board">
          {STATUSES.map((status) => {
            const items = visible.filter((tk) => tk.status === status);
            return (
              <div
                key={status}
                className={'kanban-column' + (dragOver === status ? ' drag-over' : '')}
                style={{ '--c': STATUS_COLORS[status] }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(status);
                }}
                onDragLeave={() => setDragOver(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(null);
                  const id = Number(e.dataTransfer.getData('text/plain'));
                  if (id) moveStatus(id, status);
                }}
              >
                <div className="kanban-column-header">
                  <Pill dot color={STATUS_COLORS[status]}>{t(`tickets.status_${status}`)}</Pill>
                  <span className="kanban-count">{items.length}</span>
                </div>
                <div className="kanban-column-body">
                  {items.length === 0 && <div className="tk-col-empty">—</div>}
                  {items.map((tk) => {
                    const dueStatus = dueDateStatus(tk);
                    return (
                      <div
                        key={tk.id}
                        className="kanban-card tk-kcard"
                        style={{ '--q': tk.project_color || 'var(--border)' }}
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('text/plain', String(tk.id))}
                        onClick={() => navigate(`/tickets/${tk.id}`)}
                      >
                        <div className="tk-kcard-top">
                          <span className="tk-kcard-id">#{tk.id}</span>
                          {priorityTag(tk.priority)}
                        </div>
                        <div className="kanban-card-title"><Highlight text={tk.subject} terms={terms} /></div>
                        <TagChips tags={tk.tags} />
                        <div className="tk-kcard-req">
                          <Highlight text={tk.customer_email || t('tickets.requesterStaff')} terms={terms} />
                          {tk.group_name && <> <span className="cg-tag cg-tag-sm" style={{ '--pill': tk.group_color }} title={t('tickets.customerGroup')}><i className="cg-dot" aria-hidden="true" /><Highlight text={tk.group_name} terms={terms} /></span></>}
                        </div>
                        <div className="kanban-card-meta">
                          <Pill color={tk.project_color}><Highlight text={tk.project_name} terms={terms} /></Pill>
                          {tk.sla && (tk.sla.state === 'late' || tk.sla.state === 'soon') ? slaChip(tk) : tk.due_at && (dueStatus === 'overdue' || dueStatus === 'warning') && dueChip(tk, dueStatus)}
                        </div>
                        <div className="kanban-card-actions" onClick={(e) => e.stopPropagation()}>
                          <Select value={tk.status} onChange={(e) => moveStatus(tk.id, e.target.value)}>
                            {STATUSES.map((s) => (
                              <option key={s} value={s}>{t(`tickets.status_${s}`)}</option>
                            ))}
                          </Select>
                          <span className="tk-assignees">
                            <span
                              className={'avatar avatar-sm' + (tk.assignee_id ? '' : ' avatar-empty')}
                              style={tk.assignee_id ? { background: userColor({ id: tk.assignee_id }) } : undefined}
                              title={tk.assignee_id ? `${tk.assignee_name || ''} ${tk.assignee_surname || ''}`.trim() : t('tickets.unassigned')}
                            >
                              {tk.assignee_id ? initialsOf(tk) : '—'}
                            </span>
                            {collabStack(tk)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </>)}
      </div>
    </Layout>
  );
}

export default Tickets;
